// 帳號介面：頂列帳號按鈕、登入／註冊／驗證／忘記密碼視窗、「我的心智圖」清單。
import { api, errorMessage, listMaps, deleteMap, type User, type MapSummary } from './cloud.js';
import { t, lang, type I18nKey } from './i18n.js';

type Mode = 'login' | 'register' | 'verify' | 'forgot' | 'reset';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & { className?: string } = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] {
  const element = Object.assign(document.createElement(tag), props);
  element.append(...children);
  return element;
}

export interface AccountHooks {
  onSignedIn(user: User): void | Promise<void>;
  onSignedOut(): void;
  openMap(id: string): void | Promise<void>;
  newMap(): void | Promise<void>;
  currentMapId(): string | null;
}

export class AccountUI {
  user: User | null = null;
  private mode: Mode = 'login';
  private email = '';
  private notice = '';

  constructor(private readonly slot: HTMLElement, private readonly dialog: HTMLDialogElement, private readonly mapsDialog: HTMLDialogElement, private readonly hooks: AccountHooks) {
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
    this.mapsDialog.addEventListener('click', event => { if (event.target === this.mapsDialog) this.mapsDialog.close(); });
  }

  setUser(user: User | null): void {
    this.user = user;
    this.renderSlot();
    document.querySelectorAll<HTMLElement>('[data-action="maps"]').forEach(item => { item.hidden = !user; });
  }

  // 頂列：未登入顯示「登入」；登入後顯示 email 選單。
  private renderSlot(): void {
    if (!this.user) {
      const button = el('button', { type: 'button', className: 'button primary', textContent: t('account.signIn') });
      button.addEventListener('click', () => this.open('login'));
      this.slot.replaceChildren(button);
      return;
    }
    const menu = el('details', { className: 'menu account-menu' });
    const summary = el('summary', { textContent: this.user.name || this.user.email.split('@')[0]!, title: this.user.email });
    const maps = el('button', { type: 'button', textContent: t('account.myMaps') });
    maps.addEventListener('click', () => { menu.open = false; void this.openMaps(); });
    const out = el('button', { type: 'button', textContent: t('account.signOut') });
    out.addEventListener('click', async () => {
      menu.open = false;
      await api('POST', '/api/auth/logout', {}).catch(() => undefined);
      this.setUser(null);
      this.hooks.onSignedOut();
    });
    menu.append(summary, el('div', { className: 'menu-list right' }, [el('p', { className: 'muted menu-note', textContent: this.user.email }), maps, el('hr'), out]));
    this.slot.replaceChildren(menu);
  }

  open(mode: Mode = 'login'): void {
    this.mode = mode;
    this.notice = '';
    this.render();
    if (!this.dialog.open) this.dialog.showModal();
  }

  // 依模式重建表單。
  private render(error = ''): void {
    const mode = this.mode;
    const form = el('form', { className: 'account-form' });
    form.noValidate = true;
    const field = (name: string, label: I18nKey, type: string, autocomplete: string, value = '') => {
      const input = el('input', { name, type, value, required: name !== 'name', className: 'field' });
      input.setAttribute('autocomplete', autocomplete);
      if (name === 'code') { input.inputMode = 'numeric'; input.maxLength = 6; input.pattern = '\\d{6}'; }
      return el('label', { className: 'stack' }, [el('span', { textContent: t(label) }), input]);
    };
    const head = el('header', { className: 'dialog-head' }, [el('h2', { textContent: t(`account.title.${mode}` as I18nKey) })]);
    const close = el('button', { type: 'button', className: 'icon-button', textContent: '✕' });
    close.addEventListener('click', () => this.dialog.close());
    head.append(close);
    form.append(head);
    if (mode === 'login' || mode === 'register') form.append(el('p', { className: 'muted', textContent: t('account.why') }));
    if (this.notice) form.append(el('p', { className: 'notice', textContent: this.notice }));
    if (mode === 'login') form.append(field('email', 'account.email', 'email', 'email', this.email), field('password', 'account.password', 'password', 'current-password'));
    if (mode === 'register') form.append(field('email', 'account.email', 'email', 'email', this.email), field('password', 'account.password', 'password', 'new-password'), field('name', 'account.name', 'text', 'nickname'));
    if (mode === 'verify') form.append(field('code', 'account.code', 'text', 'one-time-code'));
    if (mode === 'forgot') form.append(field('email', 'account.email', 'email', 'email', this.email));
    if (mode === 'reset') form.append(field('code', 'account.code', 'text', 'one-time-code'), field('password', 'account.newPassword', 'password', 'new-password'));
    if (error) form.append(el('p', { className: 'form-error', role: 'alert', textContent: error }));
    const submit = el('button', { type: 'submit', className: 'button primary wide', textContent: t(`account.submit.${mode}` as I18nKey) });
    form.append(submit);

    const links = el('div', { className: 'links' });
    const link = (label: I18nKey, action: () => void) => { const button = el('button', { type: 'button', className: 'link', textContent: t(label) }); button.addEventListener('click', action); links.append(button); };
    if (mode === 'login') { link('account.toForgot', () => this.switchTo('forgot', form)); link('account.toRegister', () => this.switchTo('register', form)); }
    if (mode === 'register') link('account.toLogin', () => this.switchTo('login', form));
    if (mode === 'verify') link('account.resend', () => void this.resend('/api/auth/resend'));
    if (mode === 'reset') link('account.resend', () => void this.resend('/api/auth/forgot'));
    if (mode === 'forgot') link('account.toLogin', () => this.switchTo('login', form));
    form.append(links);

    form.addEventListener('submit', event => { event.preventDefault(); void this.submit(form, submit); });
    this.dialog.replaceChildren(form);
    form.querySelector<HTMLInputElement>('input:not([value]), input[value=""]')?.focus();
  }

  private switchTo(mode: Mode, form: HTMLFormElement): void {
    const email = form.querySelector<HTMLInputElement>('input[name=email]')?.value;
    if (email) this.email = email;
    this.mode = mode;
    this.notice = '';
    this.render();
  }

  private async resend(url: string): Promise<void> {
    try {
      await api('POST', url, { email: this.email });
      this.render();
    } catch (error) {
      this.render(errorMessage(error));
    }
  }

  private async submit(form: HTMLFormElement, button: HTMLButtonElement): Promise<void> {
    const values = Object.fromEntries(new FormData(form)) as Record<string, string>;
    if (values.email) this.email = values.email.trim();
    button.disabled = true;
    try {
      switch (this.mode) {
        case 'login': {
          const { user } = await api<{ user: User }>('POST', '/api/auth/login', { email: this.email, password: values.password });
          return this.finish(user);
        }
        case 'register': {
          const result = await api<{ codeMinutes: number }>('POST', '/api/auth/register', { email: this.email, password: values.password, name: values.name });
          this.mode = 'verify';
          this.notice = t('account.codeSent', { email: this.email, minutes: result.codeMinutes });
          return this.render();
        }
        case 'verify': {
          const { user } = await api<{ user: User }>('POST', '/api/auth/verify', { email: this.email, code: values.code });
          return this.finish(user);
        }
        case 'forgot': {
          const result = await api<{ codeMinutes: number }>('POST', '/api/auth/forgot', { email: this.email });
          this.mode = 'reset';
          this.notice = t('account.codeSent', { email: this.email, minutes: result.codeMinutes });
          return this.render();
        }
        case 'reset': {
          const { user } = await api<{ user: User }>('POST', '/api/auth/reset', { email: this.email, code: values.code, password: values.password });
          return this.finish(user);
        }
      }
    } catch (error) {
      // 未驗證的帳號登入時，伺服器已重寄驗證碼，切到驗證步驟。
      if ((error as { code?: string }).code === 'unverified') {
        this.mode = 'verify';
        this.notice = errorMessage(error);
        return this.render();
      }
      this.render(errorMessage(error));
    } finally {
      button.disabled = false;
    }
  }

  private async finish(user: User): Promise<void> {
    this.dialog.close();
    this.setUser(user);
    await this.hooks.onSignedIn(user);
  }

  // 我的心智圖清單。
  async openMaps(): Promise<void> {
    const body = el('div', { className: 'maps-body' });
    const head = el('header', { className: 'dialog-head' }, [el('h2', { textContent: t('maps.title') })]);
    const close = el('button', { type: 'button', className: 'icon-button', textContent: '✕' });
    close.addEventListener('click', () => this.mapsDialog.close());
    head.append(close);
    const create = el('button', { type: 'button', className: 'button primary', textContent: t('maps.new') });
    create.addEventListener('click', async () => { this.mapsDialog.close(); await this.hooks.newMap(); });
    this.mapsDialog.replaceChildren(head, el('div', { className: 'maps-actions' }, [create]), body);
    if (!this.mapsDialog.open) this.mapsDialog.showModal();
    try {
      this.renderList(body, await listMaps());
    } catch (error) {
      body.textContent = errorMessage(error);
    }
  }

  private renderList(body: HTMLElement, maps: MapSummary[]): void {
    if (!maps.length) { body.replaceChildren(el('p', { className: 'muted', textContent: t('maps.empty') })); return; }
    const format = new Intl.DateTimeFormat(lang === 'zh-Hant' ? 'zh-TW' : 'en', { dateStyle: 'medium', timeStyle: 'short' });
    const current = this.hooks.currentMapId();
    const list = el('ul', { className: 'maps-list' });
    for (const item of maps) {
      const open = el('button', { type: 'button', className: 'map-open' }, [
        el('strong', { textContent: item.title }),
        el('span', { className: 'muted', textContent: [t('maps.updated', { time: format.format(item.updatedAt) }), item.shared ? t('maps.shared') : '', item.id === current ? t('maps.current') : ''].filter(Boolean).join(' · ') }),
      ]);
      open.addEventListener('click', async () => { this.mapsDialog.close(); await this.hooks.openMap(item.id); });
      const remove = el('button', { type: 'button', className: 'button danger small', textContent: t('maps.delete') });
      remove.disabled = item.id === current;
      remove.addEventListener('click', async () => {
        if (!confirm(t('maps.confirmDelete', { title: item.title }))) return;
        try { await deleteMap(item.id); this.renderList(body, maps.filter(other => other.id !== item.id)); }
        catch (error) { alert(errorMessage(error)); }
      });
      list.append(el('li', {}, [open, remove]));
    }
    body.replaceChildren(list);
  }
}
